import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/shadcn/alert';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Lead } from '@/types';
import type { OfferLineFormData, ParsedOfferResponse } from '@/types/forms/leads';
import { router, useForm } from '@inertiajs/react';
import { AlertTriangle, File, FileText, Plus, Trash2, Upload, X } from 'lucide-react';
import { FormEventHandler, useRef, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';
import { z } from 'zod';
import OfferLinesEditor, { emptyOfferLine, offerLinesTotal } from './OfferLinesEditor';

interface CreateOfferModalProps {
    lead: Lead;
    trigger?: React.ReactNode;
    onSuccess?: () => void;
    /** Controlled open state. Omit both to let the modal own it. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const createOfferSchema = (t: (key: string, fallback?: string) => string) =>
    z.object({
        sales_price: z
            .string()
            .min(1, t('leads.forms.create_offer.validation.sales_price_required', 'Sales price is required'))
            .refine(
                (value) => {
                    const numeric = Number(value);
                    return !Number.isNaN(numeric) && numeric >= 0;
                },
                t('leads.forms.create_offer.validation.sales_price_invalid', 'Enter a valid sales price'),
            ),
        description: z
            .string()
            .max(5000, t('leads.forms.create_offer.validation.description_max', 'Description must be less than 5000 characters'))
            .optional(),
    });

/**
 * Costs are rough working figures rather than accounted line items, so they
 * carry only a description and an amount excluding VAT.
 */
type OfferCostLineFormData = {
    description: string;
    amount: string;
    date_incurred: Date | null;
};

export default function CreateOfferModal({ lead, trigger, onSuccess, open: openProp, onOpenChange }: CreateOfferModalProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    // App-wide setting rather than a page prop: this modal is mounted from pages
    // that do not all pass one, which silently fell back to a hardcoded 10.
    const approvalThreshold = settings.leadOfferApprovalThreshold;
    // Controlled when the caller owns the open state — the lead page does, so it
    // can close its actions sheet before this one opens.
    const [internalOpen, setInternalOpen] = useState(false);
    const open = openProp ?? internalOpen;
    const setOpen = (next: boolean) => {
        setInternalOpen(next);
        onOpenChange?.(next);
    };
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [, setProfitWarning] = useState<{ show: boolean; percentage: number }>({ show: false, percentage: 0 });
    const [costLines, setCostLines] = useState<OfferCostLineFormData[]>([{ description: '', amount: '', date_incurred: new Date() }]);
    const [copyFromOfferId, setCopyFromOfferId] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Uploading the offer PDF is the default path; the form only appears once
    // there is something to review, or once the user opts to fill it in by hand.
    const [step, setStep] = useState<'upload' | 'review'>('upload');
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState<{ message: string; reason: string } | null>(null);
    const [parseSummary, setParseSummary] = useState<ParsedOfferResponse | null>(null);
    const [sourceFileName, setSourceFileName] = useState<string>('');
    const [lines, setLines] = useState<OfferLineFormData[]>([]);
    const pdfInputRef = useRef<HTMLInputElement>(null);
    // The PDF the current parse came from, so "Back to upload" can take it out of
    // the attachments again rather than silently saving an abandoned document.
    const parsedSourceFileRef = useRef<File | null>(null);
    const [pdfDragActive, setPdfDragActive] = useState(false);

    const handleParsePdf = async (file: File) => {
        setParsing(true);
        setParseError(null);
        setSourceFileName(file.name);

        try {
            const body = new FormData();
            body.append('file', file);

            // No Content-Type header: the browser has to supply the multipart
            // boundary itself.
            const response = await csrfFetch(route('leads.offers.parse-pdf', { lead: lead.id }), { method: 'POST', body });
            const payload = await response.json();

            if (!response.ok) {
                setParseError({
                    message: payload?.message ?? t('leads.offer_pdf.errors.unknown', 'This PDF could not be read.'),
                    reason: payload?.reason ?? 'unknown',
                });

                return;
            }

            const parsed: ParsedOfferResponse = payload.data;

            // Keep the source PDF, so it is stored with the offer. Parsing used to
            // consume the file and throw it away: the figures made it into the form
            // and the document the customer actually signed off existed nowhere.
            // De-duplicated by name+size in case the same PDF is parsed twice.
            setSelectedFiles((prev) => (prev.some((f) => f.name === file.name && f.size === file.size) ? prev : [...prev, file]));
            parsedSourceFileRef.current = file;

            setParseSummary(parsed);
            setLines(
                parsed.lines.map((line) => ({
                    code: line.code ?? '',
                    name: line.name,
                    quantity: line.quantity === null ? '' : String(line.quantity),
                    unit: line.unit ?? '',
                    discount: line.discount ?? '',
                    unit_price: line.unit_price === null ? '' : String(line.unit_price),
                    line_total: line.line_total === null ? '' : String(line.line_total),
                    warnings: line.warnings,
                    // Imported totals come from the document, so they must not be
                    // recomputed out from under the figures it stated.
                    touchedTotal: true,
                })),
            );
            setData('sales_price', String(parsed.suggested_sales_price));
            setData('description', parsed.description ?? '');
            setData('offer_number', parsed.offer_number ?? '');
            setData('offer_date', parsed.offer_date ?? '');
            setStep('review');
        } catch {
            setParseError({ message: t('leads.offer_pdf.errors.unknown', 'This PDF could not be read.'), reason: 'unknown' });
        } finally {
            setParsing(false);
        }
    };

    const handleFillManually = () => {
        setParseSummary(null);
        setParseError(null);
        setSourceFileName('');
        setLines([emptyOfferLine()]);
        setStep('review');
    };

    const { data, setData, processing, errors, reset } = useForm<{
        sales_price: string;
        description: string;
        offer_number: string;
        offer_date: string;
    }>({
        sales_price: '',
        description: '',
        offer_number: '',
        offer_date: '',
    });

    // Calculate total costs from offer cost lines
    const calculateTotalOfferCosts = (): number => {
        return costLines.reduce((sum, cost) => {
            const amount = Number(cost.amount) || 0;
            return sum + amount;
        }, 0);
    };

    // Calculate profit percentage using offer costs
    const calculateProfitPercentage = (salesPrice: number): number => {
        const totalCosts = calculateTotalOfferCosts();
        if (salesPrice <= 0) return 0;
        const profit = salesPrice - totalCosts;
        return (profit / salesPrice) * 100;
    };

    const handleSalesPriceChange = (value: string) => {
        setData('sales_price', value);
        updateProfitCalculation();
        clearValidationError('sales_price');
    };

    const updateProfitCalculation = () => {
        // Use a timeout to ensure state updates are reflected
        setTimeout(() => {
            const numericValue = Number(data.sales_price) || 0;
            if (numericValue > 0) {
                const totalCosts = costLines.reduce((sum, cost) => {
                    const amount = Number(cost.amount) || 0;
                    return sum + amount;
                }, 0);
                const profit = numericValue - totalCosts;
                const percentage = numericValue > 0 ? (profit / numericValue) * 100 : 0;
                setProfitWarning({
                    show: percentage < approvalThreshold,
                    percentage,
                });
            } else {
                setProfitWarning({ show: false, percentage: 0 });
            }
        }, 0);
    };

    // Handle copying costs from existing offer
    const handleCopyCostsFromOffer = (offerId: string) => {
        setCopyFromOfferId(offerId);
        if (offerId) {
            const sourceOffer = (lead.offers || []).find((o) => o.id === String(offerId));
            if (sourceOffer && sourceOffer.costLines) {
                const copiedCosts: OfferCostLineFormData[] = sourceOffer.costLines.map((cost) => ({
                    description: cost.description,
                    amount: cost.amount.toString(),
                    date_incurred: new Date(), // Always default to today
                }));
                setCostLines(copiedCosts.length > 0 ? copiedCosts : [{ description: '', amount: '', date_incurred: new Date() }]);
                updateProfitCalculation();
            }
        } else {
            setCostLines([{ description: '', amount: '', date_incurred: new Date() }]);
            updateProfitCalculation();
        }
    };

    // Handle adding new cost line
    const handleAddCostLine = () => {
        const newCostLine: OfferCostLineFormData = {
            description: '',
            amount: '',
            date_incurred: new Date(), // Always default to today
        };
        setCostLines([...costLines, newCostLine]);
        // Clear copyFromOfferId when user manually adds a cost line
        // This prevents backend from adding both copied costs and new costs
        if (copyFromOfferId) {
            setCopyFromOfferId('');
        }
    };

    // Handle removing cost line
    const handleRemoveCostLine = (index: number) => {
        setCostLines(costLines.filter((_, i) => i !== index));
        // Clear copyFromOfferId when user manually removes a cost line
        // This prevents backend from adding both copied costs and remaining costs
        if (copyFromOfferId) {
            setCopyFromOfferId('');
        }
        updateProfitCalculation();
    };

    // Handle updating existing cost line
    const handleUpdateCostLine = (index: number, field: keyof OfferCostLineFormData, value: string | Date | null) => {
        const updatedCostLines = [...costLines];
        updatedCostLines[index] = { ...updatedCostLines[index], [field]: value };
        // Ensure date_incurred is always set to today
        updatedCostLines[index].date_incurred = new Date();
        setCostLines(updatedCostLines);
        // Clear copyFromOfferId when user manually modifies cost lines
        // This prevents backend from adding both copied costs and modified costs
        if (copyFromOfferId) {
            setCopyFromOfferId('');
        }
        updateProfitCalculation();
    };

    const clearValidationError = (field: string) => {
        setValidationErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        // Validate file types and sizes
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        files.forEach((file) => {
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];
            const maxSize = 10 * 1024 * 1024; // 10MB

            if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|xls|xlsx)$/i)) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.create_offer.validation.file_invalid_type', 'Invalid file type')}`);
            } else if (file.size > maxSize) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.create_offer.validation.file_too_large', 'File too large (max 10MB)')}`);
            } else if (selectedFiles.length + validFiles.length >= 10) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.create_offer.validation.file_max_count', 'Maximum 10 files allowed')}`);
            } else {
                validFiles.push(file);
            }
        });

        if (invalidFiles.length > 0) {
            toast.error(`${t('leads.forms.create_offer.validation.files_rejected', 'Some files were rejected:')}\n${invalidFiles.join('\n')}`);
        }

        setSelectedFiles((prev) => [...prev, ...validFiles]);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();

        const result = createOfferSchema(t).safeParse(data);

        if (!result.success) {
            const fieldErrors: Record<string, string> = {};
            result.error.issues.forEach((issue) => {
                const field = issue.path[0];
                if (field) {
                    fieldErrors[field as string] = issue.message;
                }
            });
            setValidationErrors(fieldErrors);
            toast.error(t('leads.forms.create_offer.validation.resolve_errors', 'Please resolve the highlighted errors.'));
            return;
        }

        setValidationErrors({});

        // Create FormData for file uploads
        const formData = new FormData();
        formData.append('sales_price', data.sales_price);
        if (data.description) {
            formData.append('description', data.description);
        }
        if (data.offer_number) {
            formData.append('offer_number', data.offer_number);
        }
        if (data.offer_date) {
            formData.append('offer_date', data.offer_date);
        }

        // Add copy from offer ID if selected.
        // Do not send manual cost lines in this case to avoid backend-side duplication.
        if (copyFromOfferId) {
            formData.append('copy_costs_from_offer_id', copyFromOfferId);
        } else {
            const validCostLines = costLines.filter((costLine) => costLine.description.trim() && costLine.amount.trim());

            if (validCostLines.length > 0) {
                validCostLines.forEach((costLine, index) => {
                    formData.append(`cost_lines[${index}][description]`, costLine.description);
                    formData.append(`cost_lines[${index}][amount]`, costLine.amount);
                    formData.append(`cost_lines[${index}][date_incurred]`, formatDate(new Date()));
                });
            }
        }

        // Sell-side lines. A row with no name is an empty slot the user added
        // and never filled in, and would fail validation server-side.
        lines
            .filter((line) => line.name.trim())
            .forEach((line, index) => {
                formData.append(`lines[${index}][position]`, String(index + 1));
                formData.append(`lines[${index}][code]`, line.code);
                formData.append(`lines[${index}][name]`, line.name);
                formData.append(`lines[${index}][quantity]`, line.quantity || '1');
                formData.append(`lines[${index}][unit]`, line.unit);
                formData.append(`lines[${index}][discount]`, line.discount);
                formData.append(`lines[${index}][unit_price]`, line.unit_price || '0');
                formData.append(`lines[${index}][line_total]`, line.line_total || '0');
            });

        selectedFiles.forEach((file) => {
            formData.append('files[]', file);
        });

        // Use Inertia's router.post for proper state management
        router.post(`/leads/${lead.id}/offers`, formData, {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                setOpen(false);
                reset();
                setSelectedFiles([]);
                setCostLines([{ description: '', amount: '', date_incurred: new Date() }]);
                setCopyFromOfferId('');
                setProfitWarning({ show: false, percentage: 0 });
                resetPdfImport();
                // No toast here: the controller flashes one and useFlashToasts turns
                // that into the toast. Doing both showed the user two.
                onSuccess?.();
            },
            onError: (errors) => {
                console.error('Error creating offer:', errors);
                if (errors.message) {
                    toast.error(errors.message);
                } else if (errors.sales_price) {
                    toast.error(errors.sales_price);
                } else {
                    toast.error(t('leads.forms.create_offer.messages.failed_to_create', 'Failed to create offer'));
                }
            },
        });
    };

    /** Returns the modal to its upload step, discarding anything parsed. */
    const resetPdfImport = () => {
        // Drop the parsed PDF from the attachments; the user is discarding this
        // import, so the document goes with it.
        const parsed = parsedSourceFileRef.current;
        if (parsed !== null) {
            setSelectedFiles((prev) => prev.filter((f) => !(f.name === parsed.name && f.size === parsed.size)));
            parsedSourceFileRef.current = null;
        }

        setStep('upload');
        setParsing(false);
        setParseError(null);
        setParseSummary(null);
        setSourceFileName('');
        setLines([]);
        setPdfDragActive(false);
    };

    const handleClose = () => {
        setOpen(false);
        reset();
        setSelectedFiles([]);
        setCostLines([{ description: '', amount: '', date_incurred: new Date() }]);
        setCopyFromOfferId('');
        setProfitWarning({ show: false, percentage: 0 });
        setValidationErrors({});
        resetPdfImport();
    };

    const totalCosts = calculateTotalOfferCosts();
    const salesPrice = Number(data.sales_price) || 0;
    const profit = salesPrice - totalCosts;
    const profitPercentage = salesPrice > 0 ? calculateProfitPercentage(salesPrice) : 0;

    // Get existing offers for copying costs (only approved or pending)
    const availableOffers = (lead.offers || []).filter((o) => o.status === 'approved' || o.status === 'pending');

    return (
        <ResponsiveModal open={open} onOpenChange={setOpen} variant="sheet">
            {trigger && <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>}
            {/* Widest size: the offer lines table is eight columns across. */}
            <ResponsiveModalContent size="2xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="md:text-2xl md:font-bold">
                        {t('leads.forms.create_offer.title', 'Create Offer')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription className="md:text-base">
                        {t('leads.forms.create_offer.description', 'Set up a new sales offer with pricing and cost breakdown')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                {step === 'upload' ? (
                    <ResponsiveModalBody className="space-y-4">
                        {parsing ? (
                            <div className="space-y-3 rounded-lg border p-8">
                                <p className="text-sm font-medium">{t('leads.offer_pdf.parsing.headline', 'Reading the offer...')}</p>
                                <p className="text-muted-foreground text-sm">
                                    {t('leads.offer_pdf.parsing.hint', 'Pulling the line items out of :name').replace(':name', sourceFileName)}
                                </p>
                                <div className="space-y-2 pt-2">
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-3/4" />
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors md:p-12 ${
                                    pdfDragActive ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/50'
                                }`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setPdfDragActive(true);
                                }}
                                onDragLeave={() => setPdfDragActive(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setPdfDragActive(false);
                                    const dropped = e.dataTransfer.files?.[0];
                                    if (dropped) {
                                        void handleParsePdf(dropped);
                                    }
                                }}
                                onClick={() => pdfInputRef.current?.click()}
                            >
                                <Upload className="text-muted-foreground mx-auto mb-3 h-8 w-8" />
                                <p className="text-sm font-medium">{t('leads.offer_pdf.dropzone.headline', 'Drop the offer PDF here')}</p>
                                <p className="text-muted-foreground mt-1 text-sm">
                                    {t('leads.offer_pdf.dropzone.hint', 'or click to choose a file (PDF, up to 10 MB)')}
                                </p>
                                <input
                                    ref={pdfInputRef}
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                        const picked = e.target.files?.[0];
                                        if (picked) {
                                            void handleParsePdf(picked);
                                        }
                                        e.target.value = '';
                                    }}
                                />
                            </div>
                        )}

                        {parseError && (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle>{t('leads.offer_pdf.errors.title', 'Could not read this PDF')}</AlertTitle>
                                <AlertDescription>{parseError.message}</AlertDescription>
                            </Alert>
                        )}

                        {/* Offered from the start, not just after a failure: making
                            every manual offer pass through a dead end first would be
                            a worse form than the one this replaces. */}
                        {!parsing && (
                            <div className="flex justify-center">
                                <Button type="button" variant="link" size="sm" onClick={handleFillManually}>
                                    {t('leads.offer_pdf.dropzone.manual', 'Fill manually instead')}
                                </Button>
                            </div>
                        )}
                    </ResponsiveModalBody>
                ) : (
                    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                        <ResponsiveModalBody className="space-y-6 md:space-y-8">
                            {/* What was read out of the PDF, on the import path only. */}
                            {parseSummary && (
                                <div className="space-y-3 rounded-lg border p-4">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <FileText className="text-muted-foreground h-4 w-4" />
                                        <span className="text-sm font-medium">
                                            {t('leads.offer_pdf.summary.heading', 'Read from :name').replace(':name', sourceFileName)}
                                        </span>
                                        {parseSummary.offer_number && (
                                            <Badge variant="outline">
                                                {t('leads.offer_pdf.summary.offer_number', 'Offer no.')} {parseSummary.offer_number}
                                            </Badge>
                                        )}
                                        {parseSummary.offer_date && <Badge variant="outline">{parseSummary.offer_date}</Badge>}
                                        <Badge variant="outline">
                                            {t('leads.offer_pdf.summary.lines_found', ':count lines found').replace(
                                                ':count',
                                                String(parseSummary.lines.length),
                                            )}
                                        </Badge>
                                        <Badge variant={parseSummary.reconciles ? 'outline' : 'destructive'}>
                                            {parseSummary.reconciles
                                                ? t('leads.offer_pdf.summary.reconciled', 'Totals match the document')
                                                : t('leads.offer_pdf.summary.not_reconciled', 'Totals do not match the document')}
                                        </Badge>
                                    </div>
                                    {parseSummary.warnings.length > 0 && (
                                        <Alert>
                                            <AlertTriangle className="h-4 w-4" />
                                            <AlertDescription>
                                                <ul className="list-inside list-disc space-y-1">
                                                    {parseSummary.warnings.map((warning) => (
                                                        <li key={warning}>{t(`leads.offer_pdf.warnings.${warning}`, warning)}</li>
                                                    ))}
                                                </ul>
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={resetPdfImport}>
                                        {t('leads.offer_pdf.actions.back_to_upload', 'Back to upload')}
                                    </Button>
                                </div>
                            )}

                            {/* Offer lines */}
                            <div className="space-y-4">
                                <Label className="text-base font-semibold">{t('leads.offer_lines.heading', 'Offer lines')}</Label>
                                <OfferLinesEditor lines={lines} onChange={setLines} />
                            </div>

                            {/* Offer number — prefilled from the PDF, and what people
                        actually search by. */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="offer_number" className="text-base font-semibold">
                                        {t('leads.forms.create_offer.labels.offer_number', 'Offer number')}
                                    </Label>
                                    <Input
                                        id="offer_number"
                                        value={data.offer_number}
                                        onChange={(e) => setData('offer_number', e.target.value)}
                                        placeholder={t('leads.forms.create_offer.placeholders.offer_number', 'e.g. 104515')}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="offer_date" className="text-base font-semibold">
                                        {t('leads.forms.create_offer.labels.offer_date', 'Offer date')}
                                    </Label>
                                    {/* The shadcn calendar, as everywhere else — the native
                                        date input rendered the browser's own locale and its
                                        own picker, unlike the sibling edit sheet. */}
                                    <DatePicker
                                        value={data.offer_date}
                                        onValueChange={(nextValue) => setData('offer_date', nextValue)}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>
                            </div>

                            {/* Sales Price */}
                            <div className="space-y-2">
                                <Label htmlFor="sales_price" className="text-base font-semibold">
                                    {t('leads.forms.create_offer.labels.sales_price', 'Sales Price')} <span className="text-destructive">*</span>
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="sales_price"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={data.sales_price}
                                        onChange={(e) => handleSalesPriceChange(e.target.value)}
                                        placeholder={t('leads.forms.create_offer.placeholders.sales_price', '0.00')}
                                        className={`pr-10 ${validationErrors.sales_price || errors.sales_price ? 'border-destructive' : ''}`}
                                    />
                                    <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2">€</span>
                                </div>
                                {validationErrors.sales_price && <p className="text-destructive mt-1 text-sm">{validationErrors.sales_price}</p>}
                                {errors.sales_price && <p className="text-destructive mt-1 text-sm">{errors.sales_price}</p>}
                                {lines.length > 0 && Math.abs(offerLinesTotal(lines) - (Number(data.sales_price) || 0)) > 0.01 && (
                                    <Button
                                        type="button"
                                        variant="link"
                                        size="sm"
                                        className="h-auto p-0"
                                        onClick={() => handleSalesPriceChange(offerLinesTotal(lines).toFixed(2))}
                                    >
                                        {t('leads.offer_pdf.actions.use_lines_total', 'Use lines total (:amount)').replace(
                                            ':amount',
                                            formatCurrency(offerLinesTotal(lines)),
                                        )}
                                    </Button>
                                )}
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-base font-semibold">
                                    {t('leads.forms.create_offer.labels.description', 'Description')}
                                </Label>
                                <Textarea
                                    id="description"
                                    value={data.description}
                                    onChange={(e) => {
                                        setData('description', e.target.value);
                                        clearValidationError('description');
                                    }}
                                    placeholder={t('leads.forms.create_offer.placeholders.description', 'Describe what is being offered...')}
                                    className={validationErrors.description || errors.description ? 'border-destructive' : ''}
                                    rows={4}
                                    maxLength={5000}
                                />
                                {validationErrors.description && <p className="text-destructive mt-1 text-sm">{validationErrors.description}</p>}
                                {errors.description && <p className="text-destructive mt-1 text-sm">{errors.description}</p>}
                            </div>

                            {/* Cost Lines */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-base font-semibold">{t('leads.forms.create_offer.labels.cost_lines', 'Cost Lines')}</Label>
                                </div>

                                {/* Copy Costs from Existing Offer */}
                                {availableOffers.length > 0 && (
                                    <div className="mb-4">
                                        <Combobox
                                            options={availableOffers.map((offer) => ({
                                                value: offer.id.toString(),
                                                label: `${formatCurrency(offer.sales_price)} - ${offer.description || 'No description'}`,
                                                badge: offer.status,
                                            }))}
                                            value={copyFromOfferId}
                                            onValueChange={(value) => {
                                                handleCopyCostsFromOffer(value);
                                            }}
                                            placeholder={t(
                                                'leads.forms.create_offer.placeholders.copy_from_offer',
                                                'Copy costs from existing offer...',
                                            )}
                                            searchPlaceholder={t('leads.forms.create_offer.placeholders.search_offers', 'Search offers...')}
                                            emptyText={t('leads.forms.create_offer.placeholders.no_offers', 'No offers available.')}
                                            allowClear={true}
                                            clearText={t('leads.forms.create_offer.placeholders.start_fresh', 'Start fresh')}
                                        />
                                    </div>
                                )}

                                {/* Cost Lines List */}
                                <div className="space-y-4">
                                    {/* Header Labels — a column headers row only makes sense
                                once the fields sit side by side. */}
                                    <div className="hidden w-full items-center gap-3 md:flex">
                                        <div className="flex-1">
                                            <Label className="text-muted-foreground text-xs">{t('common.description', 'Description')}</Label>
                                        </div>
                                        <div className="w-40">
                                            <Label className="text-muted-foreground text-xs">
                                                {t('leads.forms.create_offer.labels.cost_amount', 'Amount excl. VAT')} (€)
                                            </Label>
                                        </div>
                                        <div className="w-10"></div>
                                    </div>

                                    {/* Cost Lines — a labelled card each on mobile, since the
                                header row above is hidden there. */}
                                    {costLines.map((costLine, index) => (
                                        <div
                                            key={index}
                                            className="flex w-full flex-col gap-3 rounded-lg border p-3 md:flex-row md:items-center md:rounded-none md:border-0 md:p-0"
                                        >
                                            <div className="flex-1 space-y-1">
                                                <Label htmlFor={`cost-line-${index}-description`} className="text-muted-foreground text-xs md:hidden">
                                                    {t('common.description', 'Description')}
                                                </Label>
                                                <Input
                                                    id={`cost-line-${index}-description`}
                                                    value={costLine.description}
                                                    onChange={(e) => handleUpdateCostLine(index, 'description', e.target.value)}
                                                    placeholder={t('leads.forms.create_offer.placeholders.cost_description', 'Cost description')}
                                                />
                                            </div>
                                            <div className="space-y-1 md:w-40">
                                                <Label htmlFor={`cost-line-${index}-amount`} className="text-muted-foreground text-xs md:hidden">
                                                    {t('leads.forms.create_offer.labels.cost_amount', 'Amount excl. VAT')} (€)
                                                </Label>
                                                <div className="relative">
                                                    <Input
                                                        id={`cost-line-${index}-amount`}
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="0.01"
                                                        min="0"
                                                        value={costLine.amount}
                                                        onChange={(e) => handleUpdateCostLine(index, 'amount', e.target.value)}
                                                        placeholder={t('leads.forms.create_offer.placeholders.amount', '0.00')}
                                                        className="pr-8"
                                                    />
                                                    <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-sm">€</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-end md:w-10 md:justify-start">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveCostLine(index)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    aria-label={t('leads.forms.create_offer.buttons.remove_cost_line', 'Remove cost line')}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add Cost Line Button */}
                                    <div className="flex justify-start pt-4">
                                        <Button type="button" variant="link" size="sm" onClick={handleAddCostLine}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            {t('leads.forms.create_offer.buttons.add_cost_line', 'Add Cost Line')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Offer Summary */}
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">
                                    {t('leads.forms.create_offer.labels.offer_summary', 'Offer Summary')}
                                </Label>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground text-sm">
                                            {t('leads.forms.create_offer.summary.total_costs', 'Total Costs')}
                                        </span>
                                        <span className="font-medium">{formatCurrency(totalCosts)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground text-sm">
                                            {t('leads.forms.create_offer.summary.profit', 'Profit')}
                                        </span>
                                        <span className={`font-medium ${profit > 0 ? 'text-success-subtle-foreground' : 'text-destructive'}`}>
                                            {formatCurrency(profit)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground text-sm">
                                            {t('leads.forms.create_offer.summary.profit_margin', 'Profit Margin')}
                                        </span>
                                        <span
                                            className={`font-medium ${profitPercentage >= approvalThreshold ? 'text-success-subtle-foreground' : 'text-warning-subtle-foreground'}`}
                                        >
                                            {profitPercentage.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>

                                {/* Approval Warning */}
                                {profitPercentage < approvalThreshold && salesPrice > 0 && (
                                    <div className="bg-warning-subtle mt-4 rounded-lg border p-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="text-warning-subtle-foreground mt-0.5 h-5 w-5 shrink-0" />
                                            <div className="flex-1">
                                                <h4 className="text-warning-subtle-foreground text-sm font-semibold">
                                                    {t('leads.forms.create_offer.approval.required', 'Approval Required')}
                                                </h4>
                                                <p className="text-warning-subtle-foreground mt-1 text-sm">
                                                    {t(
                                                        'leads.forms.create_offer.approval.message',
                                                        'This offer has a profit margin of :percentage%, which is below the :threshold% threshold. Manager approval will be required before this offer can be finalized.',
                                                    )
                                                        .replace(':percentage', profitPercentage.toFixed(2))
                                                        .replace(':threshold', approvalThreshold.toString())}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Attachments */}
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">{t('leads.forms.create_offer.labels.attachments', 'Attachments')}</Label>
                                <p className="text-muted-foreground text-sm">
                                    {t('leads.forms.create_offer.attachments.description', 'PDF, DOC, DOCX, XLS, XLSX • Max 10 files, 10MB each')}
                                </p>
                                <div
                                    className="border-muted hover:bg-muted/50 cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors md:p-12"
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const files = Array.from(e.dataTransfer.files);
                                        if (files.length > 0) {
                                            const event = { target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                                            handleFileSelect(event);
                                        }
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <div className="space-y-2">
                                        <p className="text-base font-medium md:text-lg">
                                            {t('leads.forms.create_offer.attachments.upload_files', 'Upload files')}
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            {t('leads.forms.create_offer.attachments.drag_drop', 'or drag and drop')}
                                        </p>
                                    </div>
                                </div>
                                {selectedFiles.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="bg-muted flex items-center justify-between rounded p-2">
                                                <div className="flex items-center gap-2">
                                                    <File className="h-4 w-4" />
                                                    <span className="text-sm">{file.name}</span>
                                                    <span className="text-muted-foreground text-xs">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                                </div>
                                                <Button type="button" variant="ghost" size="sm" onClick={() => removeFile(index)}>
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ResponsiveModalBody>

                        {/* Footer Buttons — pinned above the fold on a sheet. */}
                        <ResponsiveModalFooter>
                            <Button type="button" variant="outline" className="w-full md:w-auto" onClick={handleClose} disabled={processing}>
                                {t('leads.forms.create_offer.buttons.cancel', 'Cancel')}
                            </Button>
                            <Button type="submit" className="w-full md:w-auto" disabled={processing}>
                                {processing
                                    ? t('leads.forms.create_offer.buttons.saving', 'Saving...')
                                    : t('leads.forms.create_offer.buttons.create_offer', 'Create Offer')}
                            </Button>
                        </ResponsiveModalFooter>
                    </form>
                )}
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
